<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <div class="col-12">
        <div v-if="loading" class="text-center">
          <i class="fas fa-spinner fa-spin"></i>
          {{ $t("COMMON.LOADING") }}
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ $t("ERRORS.SOMETHING_WENT_WRONG") }}
        </div>

        <div v-else>
          <el-table
            :data="allSalesRows"
            :row-class-name="getRowClassName"
            header-row-class-name="thead-light"
            class="table-responsive align-items-center table-flush"
          >
            <div slot="empty" class="text-center text-muted py-4">
              {{ $t("WEEKLY_REPORTS.NO_SALES_ORDERS") }}
            </div>

            <el-table-column
              prop="recipient"
              :label="$t('COMMON.CUSTOMER')"
              min-width="200"
              class-name="customer-column"
            >
              <template slot-scope="{ row }">
                <object-link v-if="!row.isExtraLine" :object="row.recipient" />
                <!-- <span
                  v-if="!row.isExtraLine"
                  class="font-weight-bold text-primary"
                >
                  {{
                    row.recipient?.company_name || row.recipient?.name || "-"
                  }}
                </span> -->
                <base-input
                  v-else
                  v-model="row.customer_name"
                  class="extra-line-input"
                  :placeholder="$t('COMMON.CUSTOMER')"
                  @input="handleInputChange"
                  @blur="debouncedEmitUpdate"
                />
              </template>
            </el-table-column>

            <el-table-column
              prop="invoicing_date"
              :label="$t('COMMON.DATE')"
              min-width="200"
            >
              <template slot-scope="{ row }">
                <span v-if="!row.isExtraLine">
                  {{ $timeZoneDateFormat(row.invoicing_date) }}
                </span>
                <base-input v-else>
                  <flat-picker
                    :config="{
                      allowInput: true,
                      locale: $flatPickrLocale(),
                    }"
                    class="form-control datepicker"
                    v-model="row.invoicing_date"
                    @input="handleDateChange"
                  >
                  </flat-picker>
                </base-input>
              </template>
            </el-table-column>

            <el-table-column
              prop="seq_number"
              :label="$t('COMMON.INVOICE_NUMBER')"
              min-width="200"
            >
              <template slot-scope="{ row }">
                <object-link v-if="!row.isExtraLine" :object="row" />
                <el-tooltip
                  v-if="isCouponInvoice(row)"
                  :content="$t('WEEKLY_REPORTS.COUPON_INVOICE_EXCLUDED')"
                  placement="top"
                >
                  <span class="badge badge-secondary ml-2 wr-kpi-value">
                    <i class="fas fa-ticket-alt mr-1"></i>
                    {{ $t("WEEKLY_REPORTS.COUPON_INVOICE_BADGE") }}
                  </span>
                </el-tooltip>
                <!-- <span v-if="!row.isExtraLine">
                  {{ row.seq_number || row.code }}
                </span> -->
                <base-input
                  v-else
                  v-model="row.seq_number"
                  class="extra-line-input"
                  :placeholder="$t('COMMON.INVOICE_NUMBER')"
                  @input="handleInputChange"
                  @blur="debouncedEmitUpdate"
                />
              </template>
            </el-table-column>

            <el-table-column
              v-for="category in rootCategories"
              :key="category"
              :label="`${category} (${$t('COMMON.WITH_TAXES')})`"
              min-width="200"
              align="center"
              class-name="category-column"
            >
              <template v-slot="{ row }">
                <span v-if="!row.isExtraLine" class="amount-text">
                  {{
                    $formatCurrency(
                      (row.categories && row.categories[category.toLowerCase()]) || 0
                    )
                  }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>
            </el-table-column>

            <el-table-column
              prop="total_amount"
              :label="$t('COMMON.TOTAL_WITH_TAXES')"
              min-width="200"
              align="right"
              class-name="total-column"
            >
              <template slot-scope="{ row }">
                <el-tooltip
                  v-if="!row.isExtraLine && isCouponInvoice(row)"
                  :content="$t('WEEKLY_REPORTS.COUPON_INVOICE_EXCLUDED')"
                  placement="top"
                >
                  <span class="total-amount coupon-excluded-amount">
                    {{ $formatCurrency(row.pricing?.total || 0) }}
                  </span>
                </el-tooltip>
                <span v-else-if="!row.isExtraLine" class="total-amount">
                  {{ $formatCurrency(row.pricing?.total || 0) }}
                </span>
                <div v-else class="d-flex align-items-center">
                  <base-input
                    type="number"
                    step="0.01"
                    v-model="row.total_amount"
                    class="extra-line-input mr-2"
                    :placeholder="$t('COMMON.TOTAL')"
                    @input="handleInputChange"
                    @blur="debouncedEmitUpdate"
                  />
                  <base-button
                    v-if="canEdit"
                    type="danger"
                    size="sm"
                    @click="removeExtraLine(row)"
                  >
                    <i class="fas fa-trash"></i>
                  </base-button>
                </div>
              </template>
            </el-table-column>

            <el-table-column
              v-if="!canEdit"
              :label="$t('COMMON.ACTIONS')"
              min-width="100"
              align="center"
              fixed="right"
            >
              <template slot-scope="{ row }">
                <base-button
                  v-if="!row.isExtraLine"
                  type="primary"
                  icon
                  size="sm"
                  @click="viewSalesInvoice(row)"
                >
                  <i class="fas fa-eye"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- Add new line button -->
          <div class="text-center mt-3" v-if="canEdit">
            <base-button type="success" size="sm" @click="addNewLine">
              <i class="fas fa-plus mr-1"></i>
              {{ $t("WEEKLY_REPORTS.ADD_NEW_SALES_LINE") }}
            </base-button>
          </div>

          <!-- Total row
          <div class="row mt-4">
            <div class="col-12">
              <div class="card bg-default">
                <div class="card-body">
                  <div class="row">
                    <div class="col-6">
                      <h3 class="text-white">
                        {{ $t("WEEKLY_REPORTS.TOTAL_SALES_ORDERS") }}
                      </h3>
                    </div>
                    <div class="col-6 text-right">
                      <h3 class="text-white">
                        {{ $formatCurrency(grandTotal) }}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import {
  Table,
  TableColumn,
  Tag,
  Input,
  DatePicker,
  Select,
  Option,
} from "element-ui";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { debounce } from "lodash";

export default {
  name: "weekly-report-view-sales-orders",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    flatPicker,
  },

  props: {
    weeklyReport: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      salesInvoices: [],
      rootCategories: [],
      loading: true,
      error: false,
      nextExtraLineId: 1,
      userIsTyping: false,
    };
  },

  computed: {
    allSalesRows() {
      const regularRows = this.salesInvoices || [];
      const extraLines = this.weeklyReport.extra_sale_lines || [];
      return [...regularRows, ...extraLines];
    },

    grandTotal() {
      return this.allSalesRows.reduce((total, row) => {
        if (row.isExtraLine) {
          return total + parseFloat(row.total_amount || 0);
        }
        // Coupon invoices are excluded from sales (already in the cash register Z).
        if (this.isCouponInvoice(row)) {
          return total;
        }
        return total + parseFloat(row.pricing?.total || 0);
      }, 0);
    },
  },

  created() {
    this.loadData();
    // Create debounced version of update method with longer delay
    this.debouncedEmitUpdate = debounce(this.emitWeeklyReportUpdate, 2000);
    // Immediate feedback for user input
    this.optimisticUpdate = debounce(() => {
      this.userIsTyping = false;
    }, 500);
  },

  watch: {
    "weeklyReport.id": {
      handler: "loadData",
      immediate: true,
    },
  },

  methods: {
    loadData() {
      if (!this.weeklyReport.id || !this.weeklyReport.establishment) {
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = false;

      Promise.all([this.fetchCategories(), this.fetchSalesInvoices()])
        .then(() => {
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading data:", error);
          this.error = true;
          this.loading = false;
        });
    },

    async fetchCategories() {
      try {
        await this.$store.dispatch("categories/list", {
          filter: { parent: null },
        });

        const allCategories = this.$store.getters["categories/list"];
        this.rootCategories = [
          ...new Set(
            allCategories.map((cat) =>
              (cat.reporting_name
                ? `${cat.reporting_name}`
                : this.$t("WEEKLY_REPORTS.OTHER")
              ).toLowerCase()
            )
          ),
        ];
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    async fetchSalesInvoices() {
      if (!this.weeklyReport.id || !this.weeklyReport.establishment) {
        return;
      }

      try {
        // Format date range for API query
        const startDate = format(
          new Date(this.weeklyReport.starts_at),
          "yyyy-MM-dd"
        );
        const endDate = format(
          new Date(this.weeklyReport.ends_at),
          "yyyy-MM-dd"
        );

        // Fetch sales invoices with items and categories for breakdown
        await this.$store.dispatch("salesInvoices/list", {
          filter: {
            invoicing_date: [`${startDate} 00:00:00`, `${endDate} 23:59:59`],
            establishment: this.weeklyReport.establishment.id,
          },
          include:
            "recipient,salesOrder,items,items.salesInvoiceable,items.salesInvoiceable.categories",
          sort: "invoicing_date",
        });

        const invoices = this.$store.getters["salesInvoices/list"] || [];
        const filteredInvoices = invoices.filter((invoice) =>
          ["VALIDATED", "PAID"].includes(invoice.status)
        );

        this.salesInvoices = this.processInvoicesWithCategories(filteredInvoices);
      } catch (error) {
        console.error(error);
        this.error = true;
      }
    },

    processInvoicesWithCategories(invoices) {
      return invoices.map((invoice) => {
        const categories = this.categorizeInvoiceItems(invoice);
        return {
          ...invoice,
          categories,
        };
      });
    },

    categorizeInvoiceItems(invoice) {
      const invoiceItems = invoice.items || [];
      const categorizedAmounts = {};
      this.rootCategories.forEach((category) => {
        categorizedAmounts[category.toLowerCase()] = 0;
      });

      invoiceItems.forEach((item) => {
        if (!item.salesInvoiceable || !item.salesInvoiceable.categories) {
          return;
        }

        const itemAmount = parseFloat(item.pricing?.total || 0);
        const productCategories = item.salesInvoiceable.categories || [];

        let categoryKey = this.$t("WEEKLY_REPORTS.OTHER").toLowerCase();
        const productCategory = productCategories[0];
        if (productCategory) {
          categoryKey = (
            productCategory.reporting_name || this.$t("WEEKLY_REPORTS.OTHER")
          ).toLowerCase();
        }
        if (!(categoryKey in categorizedAmounts)) {
          categorizedAmounts[categoryKey] = 0;
        }
        categorizedAmounts[categoryKey] += itemAmount;
      });

      const invoiceTotal = parseFloat(invoice.pricing?.total || 0);
      const itemsTotal = Object.values(categorizedAmounts).reduce(
        (a, b) => a + b,
        0
      );
      if (itemsTotal > 0 && invoiceTotal >= 0) {
        const ratio = invoiceTotal / itemsTotal;
        Object.keys(categorizedAmounts).forEach((k) => {
          categorizedAmounts[k] =
            Math.round(categorizedAmounts[k] * ratio * 100) / 100;
        });
        const scaledSum = Object.values(categorizedAmounts).reduce(
          (a, b) => a + b,
          0
        );
        const residual = Math.round((invoiceTotal - scaledSum) * 100) / 100;
        if (residual !== 0) {
          const largest = Object.entries(categorizedAmounts).reduce(
            (a, b) => (b[1] > a[1] ? b : a)
          )[0];
          categorizedAmounts[largest] =
            Math.round((categorizedAmounts[largest] + residual) * 100) / 100;
        }
      }

      return categorizedAmounts;
    },

    getRowClassName({ row }) {
      if (row.isExtraLine) return "extra-line-row";
      if (this.isCouponInvoice(row)) return "coupon-invoice-row";
      return "";
    },

    isCouponInvoice(row) {
      return (
        !row.isExtraLine &&
        row.salesOrder &&
        row.salesOrder.pricing_type === "coupons"
      );
    },

    emitWeeklyReportUpdate() {
      // Don't emit updates while user is actively typing
      if (this.userIsTyping) {
        return;
      }
      
      // Emit update event to parent
      this.$emit("update:weeklyReport", this.weeklyReport);
    },

    handleInputChange() {
      // Indicate user is typing
      this.userIsTyping = true;
      
      // Reset typing flag after delay
      this.optimisticUpdate();
    },

    handleDateChange() {
      // Date changes should be immediate
      this.emitWeeklyReportUpdate();
    },

    addNewLine() {
      const today = new Date().toISOString().split("T")[0];
      const newLine = {
        id: `extra-sales-${this.nextExtraLineId++}`,
        isExtraLine: true,
        customer_name: "",
        created_at: today,
        seq_number: "",
        status: "VALIDATED",
        total_amount: 0,
      };

      if (!this.weeklyReport.extra_sale_lines) {
        this.weeklyReport.extra_sale_lines = [];
      }

      // Add the new line and emit event to parent
      this.weeklyReport.extra_sale_lines.push(newLine);
      this.emitWeeklyReportUpdate();
    },

    removeExtraLine(row) {
      this.weeklyReport.extra_sale_lines =
        this.weeklyReport.extra_sale_lines.filter((line) => line.id != row.id);
      this.emitWeeklyReportUpdate();
    },

    viewSalesInvoice(invoice) {
      this.$router.push({
        name: "View SalesInvoice",
        params: { id: invoice.id },
      });
    },
  },
};
</script>

<style>
.extra-line-row {
  background-color: #f7fcf9 !important;
}

.coupon-invoice-row {
  color: #8898aa;
  background-color: #f8f9fa !important;
}

.coupon-excluded-amount {
  color: #8898aa !important;
  text-decoration: line-through;
}

.wr-kpi-value {
  cursor: help;
}

.extra-line-input {
  width: 100%;
}

.total-amount {
  font-weight: 700;
  color: #172b4d;
}

.total-column {
  background-color: #f6f9fc;
}
</style>
